import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';

import { BANNER_HEIGHT, BOTTOM_BANNER_HEIGHT } from '../../constants';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const EDGE_SIZE = 8;
const CORNER_SIZE = 20;
const COLLAPSED_HEIGHT = 25;

// how far the dialog may be offscreen on left/right/bottom
const OFFSCREEN_RATIO = 0.75;

type Bounds = { left: number; right: number; top: number; bottom: number };
type Size = { width: number; height: number };
type Position = { x: number; y: number };

const computeBounds = (w: number, h: number): Bounds => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return {
    left: -OFFSCREEN_RATIO * w, // 75% off left side of screen
    right: vw - (1 - OFFSCREEN_RATIO) * w, // 75% off right side of screen
    top: 0, // never above top
    bottom: vh - (1 - OFFSCREEN_RATIO) * h, // 75% off bottom
  };
};

// extra layer of assurance that we never have modal disappear
const clampPos = (x: number, y: number, w: number, h: number): Position => {
  const b = computeBounds(w, h);

  return {
    x: Math.min(Math.max(x, b.left), b.right),
    y: Math.min(Math.max(y, b.top), b.bottom),
  };
};

type ResizeMode = 'left' | 'right' | 'bottom' | 'bl' | 'br' | null;

const MIN_LEFT_MARGIN = 20;

export interface DraggableResizableProps {
  children: ReactNode;
  style?: React.CSSProperties;
  collapsed?: boolean;
  /**
   * When provided, the modal's right edge is aligned to this x coordinate.
   * A minimum left margin of 20px is enforced so the modal is never pushed off-screen.
   * The y position defaults to BANNER_HEIGHT.
   */
  alignRightEdgeToX?: number;
  shouldBeBottomAligned?: boolean;
}

export const DraggableResizable: FC<DraggableResizableProps> = ({
  children,
  style,
  collapsed = false,
  alignRightEdgeToX,
  shouldBeBottomAligned = false,
}) => {
  // StrictMode-safe ref for react-draggable
  const nodeRef = useRef<HTMLDivElement>(null);

  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [maxSize, setMaxSize] = useState<Size>({ width: 0, height: 0 });

  // ensure dimensions stay within allowed bounds
  const clampSize = useCallback(
    (w: number, h: number): Size => ({
      width: Math.min(
        Math.max(w, MIN_WIDTH),
        maxSize.width || window.innerWidth
      ),
      height: Math.min(
        Math.max(h, MIN_HEIGHT),
        maxSize.height || window.innerHeight
      ),
    }),
    [maxSize]
  );

  const dragBounds = computeBounds(size.width, size.height);

  // Capture the 'natural' DOM size of the modal to determine initial size (which also becomes max size)
  useEffect(() => {
    if (size.width !== 0 || !nodeRef.current) return;

    const rect = nodeRef.current.getBoundingClientRect();
    const maxHeight = Math.min(rect.height, window.innerHeight * 0.8);

    setMaxSize({
      width: rect.width,
      height: maxHeight, // 80% of window height
    });

    const newSize = {
      width: rect.width,
      height: maxHeight,
    };

    setSize(newSize);

    let x, y;

    if (alignRightEdgeToX !== undefined) {
      x = Math.max(MIN_LEFT_MARGIN, alignRightEdgeToX - newSize.width);
      y = BANNER_HEIGHT;
    } else {
      x = (window.innerWidth - newSize.width) / 2;
      y = shouldBeBottomAligned
        ? window.innerHeight - newSize.height - BOTTOM_BANNER_HEIGHT
        : BANNER_HEIGHT;
    }

    setPosition(clampPos(x, y, newSize.width, newSize.height));
  }, [alignRightEdgeToX, shouldBeBottomAligned, size.width]);

  // Keep inside allowable locations when window resizes
  useEffect(() => {
    const onResize = (): void =>
      setPosition(p => clampPos(p.x, p.y, size.width, size.height));

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [size.width, size.height]);

  // --- Resize state for Pointer events (mouse or touch) ---
  const isResizing = useRef(false);
  const resizeMode = useRef<ResizeMode>(null);
  const startPointer = useRef<Position>({ x: 0, y: 0 });
  const startSize = useRef({ width: 0, height: 0 });
  const startPos = useRef<Position>({ x: 0, y: 0 });
  const pointerId = useRef<number | null>(null);

  // User clicks or touches a resize handle (edge or corner, excluding top)
  const onStartResize = useCallback(
    (e: React.PointerEvent, mode: ResizeMode) => {
      e.stopPropagation();
      isResizing.current = true;
      resizeMode.current = mode;
      pointerId.current = e.pointerId;

      startPointer.current = { x: e.clientX, y: e.clientY };
      startSize.current = { ...size };
      startPos.current = { ...position };

      document.body.style.userSelect = 'none';
      document.addEventListener('pointermove', onMove as any, {
        capture: true,
      });
      document.addEventListener('pointerup', onStop as any, { capture: true });
      document.addEventListener('pointercancel', onStop as any, {
        capture: true,
      });
    },
    [size, position]
  );

  // User moves Pointer during resize (mouse or finger is down)
  const onMove = useCallback(
    (e: PointerEvent) => {
      if (!isResizing.current) return;
      if (pointerId.current !== e.pointerId) return;

      const dx = e.clientX - startPointer.current.x;
      const dy = e.clientY - startPointer.current.y;

      let w = startSize.current.width;
      let h = startSize.current.height;
      let x = startPos.current.x;
      const y = startPos.current.y;

      // Horizontal
      if (resizeMode.current === 'right' || resizeMode.current === 'br') {
        w = startSize.current.width + dx;
      } else if (resizeMode.current === 'left' || resizeMode.current === 'bl') {
        const potential = startSize.current.width - dx;
        const nextW = clampSize(potential, h).width;
        const appliedDelta = startSize.current.width - nextW;

        w = nextW;
        x = startPos.current.x + appliedDelta;
      }

      // Vertical
      if (
        resizeMode.current === 'bottom' ||
        resizeMode.current === 'br' ||
        resizeMode.current === 'bl'
      ) {
        h = startSize.current.height + dy;
      }

      const clamped = clampSize(w, h);

      setSize(clamped);
      setPosition(clampPos(x, y, clamped.width, clamped.height));
    },
    [clampSize]
  );

  // User releases pointer after resize
  const onStop = useCallback(
    (e: PointerEvent) => {
      if (pointerId.current !== null && e.pointerId !== pointerId.current)
        return;
      isResizing.current = false;
      resizeMode.current = null;
      pointerId.current = null;

      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove as any, {
        capture: true,
      });
      document.removeEventListener('pointerup', onStop as any, {
        capture: true,
      });
      document.removeEventListener('pointercancel', onStop as any, {
        capture: true,
      });
    },
    [onMove]
  );

  // --- Drag (react-draggable) ---
  const onDrag: DraggableEventHandler = (_, data) => {
    if (isResizing.current) return false;
    setPosition(clampPos(data.x, data.y, size.width, size.height));

    return undefined;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove, { capture: true });
      document.removeEventListener('pointerup', onStop, { capture: true });
      document.removeEventListener('pointercancel', onStop, { capture: true });
    };
  }, [onMove, onStop]);

  // When collapsed, override the height
  const effectiveHeight = collapsed ? COLLAPSED_HEIGHT : size.height;

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".draggable-handle"
      position={position}
      bounds={dragBounds}
      onDrag={onDrag}
    >
      <div
        ref={nodeRef}
        style={{
          position: 'fixed',
          width: size.width == 0 ? 'auto' : size.width,
          height: effectiveHeight === 0 ? 'auto' : effectiveHeight,
          userSelect: isResizing.current ? 'none' : undefined,
          ...style,
        }}
      >
        {/* Resize edges & corners */}
        {!collapsed && (
          <>
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: EDGE_SIZE,
                cursor: 'col-resize',
                touchAction: 'none',
                userSelect: 'none',
              }}
              onPointerDown={e => onStartResize(e, 'left')}
            />
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: EDGE_SIZE,
                cursor: 'col-resize',
                touchAction: 'none',
                userSelect: 'none',
              }}
              onPointerDown={e => onStartResize(e, 'right')}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: EDGE_SIZE,
                cursor: 'row-resize',
                touchAction: 'none',
                userSelect: 'none',
              }}
              onPointerDown={e => onStartResize(e, 'bottom')}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: CORNER_SIZE,
                height: CORNER_SIZE,
                cursor: 'nesw-resize',
                touchAction: 'none',
                userSelect: 'none',
              }}
              onPointerDown={e => onStartResize(e, 'bl')}
            />
            <div
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: CORNER_SIZE,
                height: CORNER_SIZE,
                cursor: 'nwse-resize',
                touchAction: 'none',
                userSelect: 'none',
              }}
              onPointerDown={e => onStartResize(e, 'br')}
            />

            {/* draw resize handle in bottom right corner */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                right: 4,
                bottom: 4,
                width: 16,
                height: 16,
                // diagonal stripes
                background:
                  'repeating-linear-gradient(-45deg, rgba(0,0,0,.38) 0 4px, transparent 4px 8px)',
                // clip to a triangle
                clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                pointerEvents: 'none',
              }}
            />
          </>
        )}
        {children}
      </div>
    </Draggable>
  );
};
